import { ActionCardDto } from "api"
import { Font } from "components/shopFeatures/wizard/constants"
import styled, { css, keyframes } from "styled-components"
import { HomePageContainer } from "theme/shared"
import { CardDisplayOn } from "./deviceCards"

// Ticket-specified mobile breakpoint for the 2-col grid (≤820px), distinct from theme `md` (960).
const MOBILE = "@media (max-width: 820px)"
// Complement of MOBILE, so per-device card visibility flips at the same width as the layout.
const DESKTOP = "@media (min-width: 821px)"

const revealIn = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: none; }
`

export const ActionsSectionWrap = styled.section`
  width: 100%;
  background: ${(p) => p.theme.colors.neutral200};
  padding-block: 64px;

  ${MOBILE} {
    padding-block: 48px;
  }
`
export const ActionsInner = styled(HomePageContainer)`
  display: flex;
  flex-direction: column;
`
export const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start; /* flex-start = right in RTL */
  gap: 12px;
  text-align: right;
  margin-bottom: 64px;

  ${MOBILE} {
    margin-bottom: 32px;
    gap: 1px;
  }
`
export const Eyebrow = styled.span`
  font-family: ${Font.OENaPro};
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 4px;
  color: ${(p) => p.theme.colors.newPrimaryRed};
`
export const Headline = styled.h2`
  margin: 0;
  max-width: 700px;
  font-family: ${Font.OENaPro};
  font-weight: 300;
  font-size: 40px;
  line-height: 64px;
  color: ${(p) => p.theme.colors.darkBlack};
  letter-spacing: 0;

  ${MOBILE} {
    font-size: 32px;
    line-height: 2;
  }
`
export const CardsRow = styled.div<{
  $cardsCountDesktop: number
  $cardsCountMobile: number
}>`
  width: 100%;
  align-items: stretch;
  gap: 16px;
  display: grid;
  grid-template-columns: repeat(5, 1fr);

  /* Desktop: equal-width white card columns (no dividers). */
  & > * {
    flex: 1 1 0;
  }

  /* Scroll-reveal (progressive enhancement): cards are VISIBLE by default so they
     never depend on client JS. When the row scrolls into view, JS adds \`.in-view\`
     which replays a staggered entrance. If the client JS fails/slow-loads, cards
     still render. */
  &.in-view > * {
    animation: ${revealIn} 0.5s ease backwards;
  }
  ${({ $cardsCountDesktop }) => {
    return Array.from({ length: $cardsCountDesktop }, (_, i) => i + 1)
      .map(
        (n) =>
          `&.in-view > *:nth-child(${n}) { animation-delay: ${
            (n - 1) * 0.09
          }s; }`
      )
      .join("\n")
  }}

  ${MOBILE} {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    ${({ $cardsCountMobile }) => {
      return Array.from({ length: $cardsCountMobile }, (_, i) => i + 1)
        .map(
          (n) =>
            `&.in-view > *:nth-child(${n}) { animation-delay: ${
              (n - 1) * 0.09
            }s; }`
        )
        .join("\n")
    }}
  }

  @media (prefers-reduced-motion: reduce) {
    &.in-view > * {
      animation: none;
    }
  }
`
export const Card = styled.a<{
  $clickable: boolean
  $displayOn: ActionCardDto["displayOn"]
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 24px;
  padding: 44px 24px;
  text-align: center;
  text-decoration: none;
  color: inherit;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  background: ${(p) => p.theme.colors.white};
  box-shadow: 0 24px 50px rgba(0, 0, 0, 0.08);
  min-height: 150px;

  ${MOBILE} {
    padding: 32px 16px;
    gap: 12px;
  }

  /* accent line that scales in on hover */
  &::after {
    content: "";
    position: absolute;
    inset: auto 0 0;
    height: 2px;
    background: ${(p) => p.theme.colors.newPrimaryRed};
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  /* Hover (pointer devices only — no-op on touch) */
  @media (hover: hover) {
    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
    }
    &:hover::after {
      transform: scaleX(1);
    }
    &:hover .action-title {
      color: ${(p) => p.theme.colors.newPrimaryRed};
    }
  }

  /* Per-device visibility in CSS, not JS: the homepage is ISR-cached, so a
     device check at render time would flash wrong-device cards (DLK-1083). */
  ${(p) =>
    p.$displayOn === CardDisplayOn.Mobile &&
    css`
      ${DESKTOP} {
        display: none;
      }
    `}
  ${(p) =>
    p.$displayOn === CardDisplayOn.Desktop &&
    css`
      ${MOBILE} {
        display: none;
      }
    `}
`

export const CardTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
`
export const IconWrap = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${MOBILE} {
    width: 30px;
    height: 30px;
  }
`
export const CardTitle = styled.span`
  font-family: ${Font.MazdaType};
  font-weight: 400;
  font-size: 18px;
  line-height: 26px;
  color: ${(p) => p.theme.colors.darkBlack};
  transition: color 0.25s ease;
`
export const CardSubtitle = styled.span`
  font-family: ${Font.MazdaType};
  font-weight: 300;
  font-size: 14px;
  line-height: 20px;
  color: ${(p) => p.theme.colors.neutral};
`
